import { BiographyVariables } from './biographyParser';
import { ZenAfPersonalizationContext } from './zenAfPersonalizationEngine';

// DYNAMIC ZEN AF CONTENT ENGINE - Replaces static template arrays
// This system generates original content based on user biography rather than cycling through templates

export interface DynamicContentGenerators {
  createBiographicalRoast: (userDetails: BiographyVariables, context: ZenAfPersonalizationContext) => string;
  createSituationalComedy: (specificFrustration: string, userContext: BiographyVariables) => string;
  createPersonalizedDismissal: (toxicPerson: string, relationship: string) => string;
  createWorkplaceRoast: (workSituation: string, companyContext?: string) => string;
  createRelationshipComedy: (relationshipIssue: string, userContext: BiographyVariables) => string;
  createEnvironmentalMetaphor: (location: string, useCase: 'grounding' | 'comedy') => string;
  createCompoundInsult: (baseIssue: string, personalContext: string) => string;
  createTherapeuticPivot: (afterRant: string, personalStrength: string) => string;
}

export const dynamicZenAfGenerators: DynamicContentGenerators = {
  
  createBiographicalRoast: (userDetails: BiographyVariables, context: ZenAfPersonalizationContext): string => {
    const elements = [];
    
    // Mine their specific work situation
    if (userDetails.work?.company?.length > 0) {
      elements.push(`working at ${userDetails.work.company[0]} where the dysfunction flows like a broken coffee machine`);
    }
    
  // Target their actual toxic people - use available relationship data
  if (userDetails.relationships?.conflicts?.length > 0) {
    elements.push(`dealing with ${userDetails.relationships.conflicts[0]}'s premium grade-A nonsense`);
  }
    
    // Use their living situation
    if (userDetails.locations?.home?.length > 0) {
      elements.push(`living in ${userDetails.locations.home[0]} surrounded by this cosmic-level bullshit`);
    }
    
    return `BIOGRAPHICAL ROAST TARGETING: ${elements.join(', ')} - CREATE SUSTAINED 6-8 SENTENCE RANT using these SPECIFIC details, not generic templates. Build compound insults combining their actual life circumstances with creative profanity.`;
  },

  createSituationalComedy: (specificFrustration: string, userContext: BiographyVariables): string => {
    return `SITUATIONAL COMEDY GENERATION: 
Target: "${specificFrustration}"
Context: Use ${userContext.work?.company?.[0] || 'their workplace'}, ${userContext.locations?.home?.[0] || 'their living situation'}, and ${userContext.relationships?.conflicts?.[0] || 'their difficult relationships'} to create ORIGINAL metaphors and roasts about this specific frustration.
Instructions: Generate compound insults combining this exact situation with creative profanity. Build sustained comedic escalation (6-8 sentences) before therapeutic pivot. NO TEMPLATE PHRASES - create fresh expressions from their biographical details.`;
  },

  createPersonalizedDismissal: (toxicPerson: string, relationship: string): string => {
    return `PERSONALIZED DISMISSAL CREATION:
Target: "${toxicPerson}" (relationship: ${relationship})
Generate original dismissal language targeting this specific person's brand of bullshit. Create compound insults combining their particular toxic behavior with creative profanity. Build therapeutic rejection statements specific to this relationship dynamic. NO generic "Karen" templates - use their actual situation.`;
  },

  createWorkplaceRoast: (workSituation: string, companyContext?: string): string => {
    return `WORKPLACE-SPECIFIC ROAST GENERATION:
Situation: "${workSituation}"
Company Context: ${companyContext || 'workplace environment'}
Create original workplace comedy using their actual job circumstances. Generate compound insults combining workplace dysfunction with creative profanity. Build sustained rants about this specific work situation (6-8 sentences). Use their real workplace details, not generic "emails" or "meetings" unless specifically mentioned.`;
  },

  createRelationshipComedy: (relationshipIssue: string, userContext: BiographyVariables): string => {
    return `RELATIONSHIP COMEDY TARGETING:
Issue: "${relationshipIssue}"
Personal Context: ${userContext.relationships?.family_dynamics?.length ? `Family: ${userContext.relationships.family_dynamics.join(', ')}` : ''} ${userContext.relationships?.romantic?.length ? `Romantic: ${userContext.relationships.romantic.join(', ')}` : ''}
Generate relationship-specific therapeutic roasts using their actual relationship dynamics. Create personalized dismissals for their specific romantic/family drama. Build compound insults targeting this exact relationship issue, not generic dating complaints.`;
  },

  createEnvironmentalMetaphor: (location: string, useCase: 'grounding' | 'comedy'): string => {
    if (useCase === 'grounding') {
      return `ENVIRONMENTAL GROUNDING: Use "${location}" as a peaceful anchor point. Create calming references to this specific place for therapeutic grounding. Integrate naturally without repetition.`;
    } else {
      return `ENVIRONMENTAL COMEDY: Use "${location}" for location-specific metaphors and roasts. Create original comparisons using this actual place, not generic location complaints.`;
    }
  },

  createCompoundInsult: (baseIssue: string, personalContext: string): string => {
    return `COMPOUND INSULT GENERATION:
Base Issue: "${baseIssue}"
Personal Context: "${personalContext}"
Create original compound insults combining this specific issue with creative profanity. Use their personal context to make insults feel authentic to their experience. Generate fresh creative combinations, not template phrases like "dickweasel" or "ass hat."`;
  },

  createTherapeuticPivot: (afterRant: string, personalStrength: string): string => {
    return `THERAPEUTIC PIVOT CREATION:
After Rant: "${afterRant}"
Personal Strength: "${personalStrength}"
Generate empowering pivot using their actual strength and victory references. Create original therapeutic language that acknowledges their specific resilience. Build strength-based affirmations using their biographical details, not generic empowerment templates.`;
  }
};

// DYNAMIC PROMPT ENHANCEMENT SYSTEM
export const createDynamicZenAfPrompt = (
  userDetails: BiographyVariables,
  context: ZenAfPersonalizationContext,
  questionnaire: any
): string => {
  let dynamicPrompt = '\n\nDYNAMIC CONTENT GENERATION ENGINE - NO TEMPLATES ALLOWED:\n';
  
  // Generate situational comedy for each specific frustration
  if (context.specificFrustrations.length > 0) {
    dynamicPrompt += '\nSITUATIONAL COMEDY TARGETS:\n';
    context.specificFrustrations.forEach((frustration, index) => {
      dynamicPrompt += `${index + 1}. ${dynamicZenAfGenerators.createSituationalComedy(frustration, userDetails)}\n`;
    });
  }
  
  // Generate workplace-specific roasts
  if (context.workStressors.length > 0) {
    dynamicPrompt += '\nWORKPLACE ROAST GENERATION:\n';
    context.workStressors.forEach((stressor, index) => {
      const company = userDetails.work?.company?.[0];
      dynamicPrompt += `${index + 1}. ${dynamicZenAfGenerators.createWorkplaceRoast(stressor, company)}\n`;
    });
  }
  
  // Generate personalized dismissals for difficult people
  if (userDetails.relationships?.conflicts?.length > 0) {
    dynamicPrompt += '\nPERSONALIZED DISMISSAL GENERATION:\n';
    userDetails.relationships.conflicts.forEach((conflict, index) => {
      dynamicPrompt += `${index + 1}. ${dynamicZenAfGenerators.createPersonalizedDismissal(conflict, 'conflict relationship')}\n`;
    });
  }
  
  // Generate relationship comedy
  if (context.relationshipChallenges.length > 0) {
    dynamicPrompt += '\nRELATIONSHIP COMEDY GENERATION:\n';
    context.relationshipChallenges.forEach((challenge, index) => {
      dynamicPrompt += `${index + 1}. ${dynamicZenAfGenerators.createRelationshipComedy(challenge, userDetails)}\n`;
    });
  }
  
  // Generate environmental integration
  if (context.environmentalAnchors.length > 0) {
    dynamicPrompt += '\nENVIRONMENTAL INTEGRATION:\n';
    context.environmentalAnchors.forEach((anchor, index) => {
      dynamicPrompt += `${index + 1}. ${dynamicZenAfGenerators.createEnvironmentalMetaphor(anchor, 'grounding')}\n`;
    });
  }
  
  // Add biographical roast generation
  dynamicPrompt += `\nBIOGRAPHICAL ROAST ENGINE:\n`;
  dynamicPrompt += dynamicZenAfGenerators.createBiographicalRoast(userDetails, context);
  
  // Add comprehensive anti-template mandates with biographical focus
  dynamicPrompt += `\n\nANTI-TEMPLATE ENFORCEMENT - BIOGRAPHICAL ORIGINALITY MANDATE:
- BANNED PHRASES: Avoid all generic template phrases - use their biographical details for authentic personalization
- BIOGRAPHICAL MANDATE: Every creative element must be derived from their actual life circumstances
- ORIGINAL EXPRESSION: Generate fresh therapeutic language combinations using their real-world context
- PERSONALIZATION DEPTH: Target their specific people, places, work situations, and relationship dynamics
- SUSTAINED AUTHENTICITY: Create meaningful comedic content from their unique biographical narrative
- THERAPEUTIC INTEGRATION: All creative language serves their healing through genuine personalized validation`;
  
  return dynamicPrompt;
};

// ORIGINALITY VALIDATION SYSTEM
export const validateDynamicOriginality = (
  content: string, 
  userDetails: BiographyVariables,
  context: ZenAfPersonalizationContext
): {
  originalityScore: number;
  personalizationScore: number;
  templateViolations: string[];
  personalizationGaps: string[];
  overallQuality: 'excellent' | 'good' | 'needs_improvement' | 'poor';
} => {
  // Import banned phrases from centralized config
  const bannedTemplates = [
    'intern', 'dickweasel', 'ass hat', 'Karen', 'Chad',
    'discount fog machine', 'soggy biscuit', 'drunk penguin', 'judging cat',
    'WiFi connection having a breakdown', 'Satan\'s intern', 'glittery ass hat',
    'premium grade-A organic', 'caffeinated hamster', 'disappointed cat who caught you masturbating',
    'clusterfuck', 'bullshit buffet', 'artisanal horseshit', 'expired coupon'
  ];
  
  const templateViolations = bannedTemplates.filter(template => 
    content.toLowerCase().includes(template.toLowerCase())
  );
  
  // Check for biographical personalization
  const personalizationElements = [];
  const personalizationGaps = [];
  
  // Check for work personalization
  if (userDetails.work?.company?.length > 0) {
    const hasWorkPersonalization = userDetails.work.company.some(company => 
      content.toLowerCase().includes(company.toLowerCase())
    );
    if (hasWorkPersonalization) {
      personalizationElements.push('work');
    } else {
      personalizationGaps.push('Missing work-specific personalization');
    }
  }
  
  // Check for location personalization
  if (userDetails.locations?.home?.length > 0) {
    const hasLocationPersonalization = userDetails.locations.home.some(location => 
      content.toLowerCase().includes(location.toLowerCase())
    );
    if (hasLocationPersonalization) {
      personalizationElements.push('location');
    } else {
      personalizationGaps.push('Missing location-specific references');
    }
  }
  
  // Check for difficult people personalization
  if (userDetails.relationships?.conflicts?.length > 0) {
    const hasConflictPersonalization = userDetails.relationships.conflicts.some(conflict => 
      content.toLowerCase().includes(conflict.toLowerCase())
    );
    if (hasConflictPersonalization) {
      personalizationElements.push('conflicts');
    } else {
      personalizationGaps.push('Missing personalized dismissals for their difficult relationships');
    }
  }
  
  // Check for specific frustration targeting
  if (context.specificFrustrations.length > 0) {
    const hasFrustrationPersonalization = context.specificFrustrations.some(frustration => 
      content.toLowerCase().includes(frustration.toLowerCase().substring(0, 20))
    );
    if (hasFrustrationPersonalization) {
      personalizationElements.push('frustrations');
    } else {
      personalizationGaps.push('Missing specific frustration targeting');
    }
  }
  
  // Calculate scores
  const originalityScore = Math.max(0, 100 - (templateViolations.length * 15));
  const personalizationScore = Math.min(100, personalizationElements.length * 25);
  
  const overallScore = (originalityScore + personalizationScore) / 2;
  
  let overallQuality: 'excellent' | 'good' | 'needs_improvement' | 'poor';
  if (overallScore >= 85) overallQuality = 'excellent';
  else if (overallScore >= 70) overallQuality = 'good';
  else if (overallScore >= 50) overallQuality = 'needs_improvement';
  else overallQuality = 'poor';
  
  return {
    originalityScore,
    personalizationScore,
    templateViolations,
    personalizationGaps,
    overallQuality
  };
};